import { Suspense } from 'react';
import { AppShell, Box, MantineColor, MantineSpacing } from '@mantine/core';
import PageLoader from 'src/UI/loader/PageLoader';
import { useFavicon } from '@mantine/hooks';
import { useCallback, useRef, useState } from 'react';
import { Outlet } from 'react-router';
import { ClientsProvider } from 'src/clients/context/ClientsContext';
import {
  CurrentClientProvider,
  useCurrentClientIfAvailable,
} from 'src/clients/context/CurrentClientProvider';
import DataExportProvider from 'src/data-export/DataExportProvider';
import AgendaProvider from 'src/meeting-agenda/AgendaProvider';
import MeetingNoteProvider from 'src/meeting-note/MeetingNoteProvider';
import SuitabilityProvider from 'src/suitability-report/SuitabilityProvider';
import { AppBreadcrumbs } from './AppBreadcrumbs';
import Chat from './chat/Chat';
import Header, { HEADER_HEIGHT } from './header/Header';
import { UsersProvider } from 'src/admin/users/context/UsersContext';
import { AppContainerProvider } from './AppContainerContext';

type Props = {
  children?: React.ReactNode;
  showChat?: boolean;
  bg?: MantineColor;
  p?: MantineSpacing;
};

const DEFAULT_CHAT_SIZE = 350;
const MIN_WIDTH = 200;
const savedWidth = Number(localStorage.getItem('draggableWidth') || DEFAULT_CHAT_SIZE);
export default function DeprecatedAppContainer({
  children,
  showChat = true,
  p = 'md',
  bg = 'contentBackground',
  ...pageProps
}: Props) {
  useFavicon('https://public-afternoon-bucket.s3.eu-north-1.amazonaws.com/smallLogo.svg');

  const [chatCollapsed, setChatCollapsed] = useState(!showChat);
  const [chatWidth, setChatWidth] = useState(
    savedWidth > MIN_WIDTH ? savedWidth : DEFAULT_CHAT_SIZE,
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastStoredWidth = useRef<number | null>(null);
  const handleChatDrag = useCallback((newWidth: number) => {
    let targetWidth = newWidth;

    if (targetWidth < MIN_WIDTH) {
      setChatCollapsed(true);
      targetWidth = 200;
    }

    setChatWidth(targetWidth);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      localStorage.setItem('draggableWidth', targetWidth.toString());
      lastStoredWidth.current = targetWidth;
    }, 500);
  }, []);

  const content = children ? children : <Outlet />;

  const handleShowChat = useCallback(() => {
    setChatCollapsed(false);
  }, [setChatCollapsed]);

  const handleHideChat = useCallback(() => {
    setChatCollapsed(true);
  }, [setChatCollapsed]);

  return (
    <CurrentClientProvider>
      <ClientsProvider>
        <SuitabilityProvider>
          <DataExportProvider>
            <AgendaProvider>
              <MeetingNoteProvider>
                <UsersProvider>
                  <AppContainerProvider>
                    <DeprecatedAfternoonAppShell
                      chatWidth={chatWidth}
                      chatCollapsed={chatCollapsed}
                      handleChatDrag={handleChatDrag}
                      handleShowChat={handleShowChat}
                      handleHideChat={handleHideChat}
                      pageProps={pageProps}
                      p={p}
                      bg={bg}
                      content={content}
                    />
                  </AppContainerProvider>
                </UsersProvider>
              </MeetingNoteProvider>
            </AgendaProvider>
          </DataExportProvider>
        </SuitabilityProvider>
      </ClientsProvider>
    </CurrentClientProvider>
  );
}

type AppShellProps = {
  chatWidth: number;
  chatCollapsed: boolean;
  handleChatDrag: (width: number) => void;
  handleShowChat: () => void;
  handleHideChat: () => void;
  p: MantineSpacing;
  bg: MantineColor;
  pageProps: Omit<Props, 'children' | 'showChat' | 'bg'>;
  content: React.ReactNode;
};

const DeprecatedAfternoonAppShell = ({
  chatWidth,
  chatCollapsed,
  handleChatDrag,
  handleShowChat,
  handleHideChat,
  pageProps,
  content,
  p = 'md',
  bg,
}: AppShellProps) => {
  const { clientId } = useCurrentClientIfAvailable();
  const width = clientId ? chatWidth : 0;
  return (
    <AppShell
      header={{
        height: HEADER_HEIGHT,
      }}
      aside={{
        width,
        breakpoint: 'md',
        collapsed: { desktop: chatCollapsed || chatWidth < 200, mobile: true },
      }}
    >
      <Header showChat={handleShowChat} isChatOpened={!chatCollapsed} />
      <AppShell.Main bg={bg} {...pageProps}>
        <Box p={p}>
          <AppBreadcrumbs />
          <Suspense fallback={<PageLoader absolute={false} />}>{content}</Suspense>
        </Box>
      </AppShell.Main>
      <Chat clientId={clientId} width={width} onDrag={handleChatDrag} onHideChat={handleHideChat} />
    </AppShell>
  );
};
